import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LanguageContext } from './language-context';

const translations = {
  en: {
    // Header
    'header.bookDemo': 'Book Demo',
    'header.joinPilot': 'Join Pilot',
    'header.about': 'About',
    'header.pricing': 'Pricing',
    'header.signIn': 'Sign In',

    // Hero
    'hero.badge': 'AI Product Experience for Cosmetics',
    'hero.title': 'AI customer service',
    'hero.titleLine2': 'for cosmetic brands.',
    'hero.subtitle': 'That turns every product into a living support experience.',
    'hero.benefit1': 'Build customer trust',
    'hero.benefit2': 'Reduce support tickets',
    'hero.benefit3': 'One AI per product',
    'hero.support1': 'Ingredients & certifications on demand',
    'hero.support2': 'Tutorials and usage guidance',
    'hero.support3': 'Live chat via WhatsApp & Instagram',
    'hero.support4': 'Scan QR on packaging',
    'hero.cta': 'Schedule demo',
    'hero.ctaSecondary': 'Contact us',

    // Credibility Strip
    'credibility.title': 'Designed for Southeast Asian cosmetic brands and maklons.',
    'credibility.backedBy': 'Backed by',
    'credibility.metaPartner': 'Official',

    // QR Code Generator Section
    'qrCode.badge': 'QR CODE GENERATOR',
    'qrCode.title': 'One QR. Instant support.',
    'qrCode.subtitle': 'Create QR codes for each product. Customers scan, land on a branded page, and get instant AI help.',
    'qrCode.products': 'Products',
    'qrCode.active': 'Active',
    'qrCode.draft': 'Draft',
    'qrCode.selectProduct': 'Select product to create QR',
    'qrCode.createQR': 'Create QR Code',
    'qrCode.qrFor': 'QR code for landing page',
    'qrCode.landingPage': 'Product Landing Page',
    'qrCode.aiSupport': 'AI Support, 24/7',
    'qrCode.downloadQR': 'Download QR Code',
    'qrCode.scanInfo': 'Customers scan & land here',
    'qrCode.chatWith': 'Chat with CAIRE',
    'qrCode.available': 'Available 24/7',
    'qrCode.aiChat': 'AI Chat + Certifications',
    'qrCode.downloadAll': 'All can be downloaded',
    'qrCode.feature1': 'Unique per product',
    'qrCode.feature1Desc': 'Each product has its own QR',
    'qrCode.feature2': 'Branded pages',
    'qrCode.feature2Desc': 'Your logo, your product',
    'qrCode.feature3': 'Instant AI support',
    'qrCode.feature3Desc': 'Scan → Chat → Done',

    // QR Generator (new keys)
    'qrGenerator.badge': 'QR CODE GENERATOR',
    'qrGenerator.title': 'One QR. Instant support.',
    'qrGenerator.subtitle': 'Create QR codes for each product. Customers scan, land on a branded page, and get instant AI help.',
    'qrGenerator.products': 'Products',
    'qrGenerator.active': 'Active',
    'qrGenerator.generateTitle': 'Generate QR Code',
    'qrGenerator.generateDesc': 'For Everwhite Axillary Cream',
    'qrGenerator.landingPage': 'Product Landing Page',
    'qrGenerator.landingPageDesc': 'AI Support, 24/7',
    'qrGenerator.downloadQR': 'Download QR Code',
    'qrGenerator.customerScans': 'Customer scans & lands here',
    'qrGenerator.chatWithCaire': 'Chat with CAIRE',
    'qrGenerator.available247': 'Available 24/7',
    'qrGenerator.aiChatCerts': 'AI Chat + Certifications',
    'qrGenerator.allDownloadable': 'All can be downloaded',
    'qrGenerator.uniquePerProduct': 'Unique per product',
    'qrGenerator.uniqueDesc': 'Each product has its own QR',
    'qrGenerator.brandedPages': 'Branded pages',
    'qrGenerator.brandedDesc': 'Your logo, your product',
    'qrGenerator.instantSupport': 'Instant AI support',
    'qrGenerator.instantDesc': 'Scan → Chat → Done',

    // Omnichannel Inbox Section
    'inbox.badge': 'OMNICHANNEL INBOX',
    'inbox.title': 'One inbox. All channels.',
    'inbox.subtitle': 'WhatsApp, Instagram, Messenger, and website chatbox — all conversations in one place. AI handles the volume, you stay in control.',
    'inbox.ai': 'AI',
    'inbox.search': 'Search',
    'inbox.ongoing': 'Ongoing',
    'inbox.aiCustomerSummary': 'AI Customer Summary',
    'inbox.customerInfo': 'Customer Info',
    'inbox.conversationFlow': 'Conversation Flow',
    'inbox.greetingContext': 'Greeting & Context',
    'inbox.informationRequest': 'Information Request',
    'inbox.privacyNotice': 'Privacy Notice',
    'inbox.solutionProvided': 'Solution Provided',
    'inbox.humanEscalation': 'Human Escalation',
    'inbox.done': 'Done',
    'inbox.active': 'Active',
    'inbox.pending': 'Pending',
    'inbox.handledByAI': 'Handled by AI Agent',
    'inbox.aiHandling': 'AI is handling — Take over to respond',
    'inbox.allConversations': 'All conversations flow into one inbox',
    'inbox.qrProductPage': 'QR Product Page',
    'inbox.whatsapp': 'WhatsApp',
    'inbox.instagram': 'Instagram',
    'inbox.messenger': 'Messenger',
    'inbox.webChat': 'Web Chat',

    // AI Inbox (new keys)
    'aiInbox.badge': 'OMNICHANNEL INBOX',
    'aiInbox.title': 'One inbox. All channels.',
    'aiInbox.subtitle': 'WhatsApp, Instagram, Messenger, and website chatbox — all conversations in one place. AI handles the volume, you stay in control.',
    'aiInbox.inbox': 'Inbox',
    'aiInbox.customerInfo': 'Customer Info',
    'aiInbox.conversationFlow': 'Conversation Flow',
    'aiInbox.aiHandling': 'AI is handling — Take over to respond',
    'aiInbox.allFlowIntoOne': 'All conversations flow into one inbox',
    'aiInbox.qrProductPages': 'QR Product Pages',

    // Website Chat Widget Section
    'chatWidget.badge': 'WEBSITE CHAT WIDGET',
    'chatWidget.title': 'Embed CAIRE on your website',
    'chatWidget.subtitle': 'Give your customers instant AI-powered support directly on your website. No app downloads, no redirects — just seamless conversations.',
    'chatWidget.onlineNow': 'Online now',
    'chatWidget.typeMessage': 'Type a message...',
    'chatWidget.poweredBy': 'Powered by Caire',
    'chatWidget.feature1': 'One-line embed code',
    'chatWidget.feature2': 'Matches your brand',
    'chatWidget.feature3': 'Image uploads',
    'chatWidget.feature4': '24/7 AI responses',

    // AI Page Builder Section
    'pageBuilder.badge': 'AI PAGE BUILDER',
    'pageBuilder.title': 'Build product pages with AI',
    'pageBuilder.subtitle': 'Each product has its own landing page. Edit with natural language — no coding.',
    'pageBuilder.editor': 'Landing Page Editor',
    'pageBuilder.builder': 'Landing Page Builder',
    'pageBuilder.pageBuilderAI': 'Page Builder AI',
    'pageBuilder.askToChange': 'Ask me to change your landing page',
    'pageBuilder.preview': 'Preview',
    'pageBuilder.chatOrTalk': 'Chat or Talk with CAIRE',
    'pageBuilder.available247': 'Available 24/7',
    'pageBuilder.halal': 'Halal',
    'pageBuilder.bpom': 'BPOM',
    'pageBuilder.explainChange': 'Explain what you want to change...',
    'pageBuilder.feature1': 'Edit with AI',
    'pageBuilder.feature2': 'Instant preview',
    'pageBuilder.feature3': 'No coding',
    'pageBuilder.feature4': 'Multi-language support',

    // AI Page Builder (new keys)
    'aiPageBuilder.badge': 'AI PAGE BUILDER',
    'aiPageBuilder.title': 'Build product pages with AI',
    'aiPageBuilder.subtitle': 'Each product has its own landing page. Edit with natural language — no coding.',
    'aiPageBuilder.landingPageBuilder': 'Landing Page Builder',
    'aiPageBuilder.pageBuilderAI': 'Page Builder AI',
    'aiPageBuilder.askToModify': 'Ask me to modify your landing page',
    'aiPageBuilder.describeChange': 'Describe what you want to change...',
    'aiPageBuilder.preview': 'Preview',
    'aiPageBuilder.feature1': 'Edit with AI',
    'aiPageBuilder.feature2': 'Instant preview',
    'aiPageBuilder.feature3': 'No coding',
    'aiPageBuilder.feature4': 'Multi-language support',

    // CAIRE Comparison
    'comparison.badge': 'Confusion → Confidence',
    'comparison.title': 'The product isn\'t the problem — the experience is.',
    'comparison.subtitle': 'Customers don\'t return products because they\'re bad. They return them because they couldn\'t get answers fast enough.',
    'comparison.headerBefore': 'Without CAIRE',
    'comparison.headerAfter': 'With CAIRE',
    'comparison.item1Before': '"Is this ingredient safe for my skin?"',
    'comparison.item1After': 'Instant ingredient & safety info via chat',
    'comparison.item2Before': '"Confused how to use this serum"',
    'comparison.item2After': 'Step-by-step tutorial on WhatsApp',
    'comparison.item3Before': 'DMs flooded with basic questions',
    'comparison.item3After': '70% of questions answered automatically',
    'comparison.item4Before': 'No proof of halal/BPOM certification',
    'comparison.item4After': 'Certifications displayed instantly',
    'comparison.item5Before': 'Bad reviews from confused customers',
    'comparison.item5After': 'Higher satisfaction & repeat purchase',
    'comparison.item6Before': 'No idea what customers are asking',
    'comparison.item6After': 'Real insights from every product',
    'comparison.footer': 'Built for cosmetic brands that care about customer trust.',
    'comparison.cta': 'Get started',

    // Platform Integrations
    'integrations.badge': 'Platform Integration',
    'integrations.title': 'Connect your product catalog in minutes',
    'integrations.description': 'Product data in — AI product agents out. No manual setup.',

    // Meta Partner Banner
    'metaPartner.badge': 'Official Meta Tech Partner',
    'metaPartner.description': 'Official Meta Partner delivering intelligent, AI-powered customer support through WhatsApp Business — powered by CAIRE.',

    // Testimonials - Cosmetics focused
    'testimonials.title': 'Real questions, instant answers',
    'testimonials.subtitle': 'See how CAIRE helps cosmetic customers get answers instantly',
    'testimonials.quote1': '"Our maklon clients love it because their brands now have instant product support. Makes their products feel premium."',
    'testimonials.author1': '',
    'testimonials.quote2': '"Customers used to DM us constantly about ingredients. Now CAIRE handles 80% of those questions automatically."',
    'testimonials.author2': '',
    'testimonials.quote3': '"We reduced return rates by showing halal and BPOM certificates instantly through the chatbot."',
    'testimonials.author3': '',

    // Pricing
    'pricing.title': 'Pricing that grows with your product',
    'pricing.subtitle': 'Start free. Add white-label and automation as you grow.',
    'pricing.noContracts': 'No long-term contracts. Upgrade anytime.',
    'pricing.planFree': 'Start',
    'pricing.planGrowth': 'Growth',
    'pricing.planEnterprise': 'Enterprise',
    'pricing.freeTagline': 'Quick start',
    'pricing.growthTagline': 'Scale as you grow',
    'pricing.enterpriseTagline': 'Automation at scale',
    'pricing.freePeriod': '',
    'pricing.growthPeriod': '',
    'pricing.enterprisePeriod': '',
    'pricing.freePrice': 'Free',
    'pricing.growthPrice': 'Pay As You Go',
    'pricing.enterprisePrice': 'Custom',
    'pricing.freeDescription': 'Launch your first product AI assistant in minutes. No risk, no credit card.',
    'pricing.growthDescription': 'Scale based on actual usage. No fixed monthly fees.',
    'pricing.enterpriseDescription': 'White-label AI for your entire product catalog — built for OEMs, enterprises & regulated markets.',
    'pricing.freeFeature1': 'Up to 5 AI product agents',
    'pricing.freeFeature2': 'CAIRE WhatsApp/Instagram/Messenger/Chatbox agent',
    'pricing.freeFeature3': 'Product knowledge training',
    'pricing.freeFeature4': 'QR activation per product',
    'pricing.freeFeature5': 'Conversation history',
    'pricing.freeFeature6': 'Slack integration',
    'pricing.freeFeature7': 'AI agent in 1 language',
    'pricing.freeFeature8': '1 team seat',
    'pricing.freeFeature9': 'Email support',
    'pricing.growthFeature1': 'Pay only what you use',
    'pricing.growthFeature2': 'White-label WhatsApp/Instagram/Messenger/Chatbox agent',
    'pricing.growthFeature3': 'Brand-owned WhatsApp/Instagram/Messenger',
    'pricing.growthFeature4': 'Workflow builder',
    'pricing.growthFeature5': 'Analytics Lite',
    'pricing.growthFeature6': 'Agent handover',
    'pricing.growthFeature7': 'Shopify, Zendesk & Intercom',
    'pricing.growthFeature8': 'Multi-language AI agent',
    'pricing.growthFeature9': '3 team seats',
    'pricing.enterpriseFeature1': 'Unlimited AI product agents',
    'pricing.enterpriseFeature2': 'Advanced workflow automation',
    'pricing.enterpriseFeature3': 'Custom integrations & API access',
    'pricing.enterpriseFeature4': 'Web + WhatsApp/Instagram/Messenger unified',
    'pricing.enterpriseFeature5': 'Team management & audit logs',
    'pricing.enterpriseFeature6': 'Advanced analytics',
    'pricing.enterpriseFeature7': 'Priority routing & escalation',
    'pricing.enterpriseFeature8': 'Multi-language AI agent',
    'pricing.enterpriseFeature9': 'Data region EU/US/Asia',
    'pricing.enterpriseFeature10': 'SSO (Okta, Google)',
    'pricing.enterpriseFeature11': 'SLA & uptime guarantee',
    'pricing.enterpriseFeature12': 'Dedicated success manager',
    'pricing.freeCta': 'Join waitlist',
    'pricing.growthCta': 'Get started',
    'pricing.enterpriseCta': 'Book demo',
    'pricing.seeFullPricing': 'See full pricing',

    // Pricing Calculator
    'calculator.title': 'Estimate your cost',
    'calculator.subtitle': 'Simple and transparent pricing based on usage',
    'calculator.whatsappLabel': 'WhatsApp/Instagram/Messenger/Chatbox chats per month',
    'calculator.estimatedCost': 'Estimated monthly cost',
    'calculator.perMonth': 'per month',
    'calculator.savingsTitle': 'Estimated Monthly Savings',
    'calculator.savingsSubtitle': 'What this means for your brand',
    'calculator.benefit1': 'Your customers get instant WhatsApp/Instagram/Messenger/Chatbox help 24/7',
    'calculator.benefit2': 'Your team saves {hours}+ hours per month',
    'calculator.benefit3': 'You cut manual support costs by 85%',

    // Feature Comparison
    'comparison.features': 'Features',
    'comparison.getStarted': 'Get started',
    'comparison.growth': 'Growth',
    'comparison.enterprise': 'Enterprise',
    'comparison.aiAgents': 'AI product agents',
    'comparison.basePrice': 'Base price',
    'comparison.whatsappAgent': 'AI WhatsApp/Instagram/Messenger/Chatbox agent',
    'comparison.qrActivation': 'QR activation per product',
    'comparison.workflowBuilder': 'AI workflow builder',
    'comparison.handover': 'Handover to support team',
    'comparison.shopifyIntegration': 'Shopify integration',
    'comparison.zendeskIntercom': 'Zendesk / Intercom',
    'comparison.analytics': 'Analytics',
    'comparison.teamSeats': 'Team seats',
    'comparison.apiIntegration': 'API & custom integration',
    'comparison.ssoSla': 'SSO, SLA, Data region (EU/US/Asia)',
    'comparison.security': 'Security & governance',
    'comparison.dpa': 'Data processing agreement (DPA)',
    'comparison.slaUptime': 'SLA (uptime)',
    'comparison.compliance': 'Compliance certifications',
    'comparison.dataRetention': 'Data retention',
    'comparison.upTo5': 'Up to 5',
    'comparison.unlimited': 'Unlimited',
    'comparison.free': 'Free',
    'comparison.payAsYouGo': 'Pay as you go',
    'comparison.custom': 'Custom',
    'comparison.lite': 'Lite',
    'comparison.advanced': 'Advanced',
    'comparison.days14': '14 days',
    'comparison.days180': '180 days',
    'comparison.months24plus': '24 months+',
    'comparison.soc2gdpr': 'SOC2, GDPR',
    'comparison.soc2iso27001': 'SOC2, ISO 27001, GDPR',
    'comparison.uptime995': '99.5%',

    // Pricing FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.q1': 'Can I start without any engineering work?',
    'faq.a1': 'Absolutely. All CAIRE plans are completely no-code — that\'s why we built CAIRE. Just upload your product information, train the AI, and start helping customers in minutes. No technical setup, no integrations, no code.',
    'faq.q2': 'What\'s the difference between CAIRE agent and White-label agent?',
    'faq.a2': 'CAIRE Agent (WhatsApp/Instagram/Messenger/Chatbox)\nCustomers talk to your support via WhatsApp/Instagram/Messenger/Chatbox with CAIRE branding. Fast to launch, low-friction, perfect for pilots.\n\nWhite-label Agent\nCustomers talk to "you" on your own channels (WhatsApp/Instagram/Messenger/Chatbox), with your logo and branding. Feels like your own support team powered by CAIRE.',
    'faq.q3': 'Can I use my existing WhatsApp/Instagram/Messenger accounts?',
    'faq.a3': 'Yes. On Growth and Enterprise plans you can connect your existing WhatsApp Business number, Instagram Business account, and Messenger to CAIRE, so customers keep using the same channels. We handle the integration to make it seamless.\n\nPrefer to keep your old accounts for internal use?\nNo problem. We can also set up new channels for your CAIRE agent.',
    'faq.q4': 'Do you support ticketing systems like Zendesk?',
    'faq.a4': 'Yes. You can keep using tools like Zendesk, Intercom.\nCAIRE becomes the first layer of your product support on WhatsApp/Instagram/Messenger/Chatbox.\n\nCustomers don\'t need to fill forms or create tickets themselves.\nCAIRE gathers the details, solves what it can, and automatically creates a ticket in your system if needed, with all the conversation context attached.\n\nYour support team picks up where CAIRE left off — without disrupting your workflow.',
    'faq.q6': 'Do you support multiple languages?',
    'faq.a6': 'Yes. CAIRE AI agents are multilingual.\n\nFree plan supports one language.\n\nGrowth and Enterprise unlock multi-language support, so your customers can get help in their preferred language automatically.\n\nWe currently support:\n- Bahasa Indonesia\n- English\n- Nederlands\n- Español\n- Français\n- Deutsch\n\nMore languages coming soon.',
    'faq.q7': 'Do you provide local support in Indonesia?',
    'faq.a7': 'Absolutely! We\'re based in Indonesia to ensure you get the best support. Our Indonesian team understands the local market and can assist you with implementation, technical support, and training. We\'re here to help you succeed with CAIRE.',
    'faq.q8': 'Do you provide local support in Europe?',
    'faq.a8': 'Yes, we are based in the Netherlands and have a team ready to support you across Europe. We understand the European market and can assist you with implementation, technical support, and training in your region.',


    // Pricing CTA
    'pricingCta.title': 'Get started now',
    'pricingCta.subtitle': 'Transform your product support with AI that understands your products.',
    'pricingCta.button': 'Schedule demo',

    // Enterprise Contact
    'enterprise.title': 'AI support for enterprise scale',
    'enterprise.subtitle': 'Custom integration, security review, and onboarding included.',
    'enterprise.subtitleLine2': 'Talk to our team to design your ideal deployment.',
    'enterprise.firstNameLabel': 'First name',
    'enterprise.lastNameLabel': 'Last name',
    'enterprise.emailLabel': 'Email*',
    'enterprise.submitButton': 'Submit',
    'enterprise.submitting': 'Submitting...',
    'enterprise.successTitle': 'Thank you for your interest!',
    'enterprise.successMessage': 'Your submission has been received. Our team will be in touch within 24 hours to discuss your enterprise needs.',

    // Footer
    'footer.terms': 'Terms & Conditions',
    'footer.privacy': 'Privacy',
    'footer.copyright': '© 2026 CAIRE. All rights reserved.',

    // About Page
    'about.badge': 'Our Story',
    'about.hero.title': 'About CAIRE',
    'about.hero.subtitle': 'Instant product help via QR, WhatsApp, Instagram & Messenger — no apps, no waiting.',
    'about.hero.description': 'CAIRE puts real support inside the product. Scan a QR, send a WhatsApp, Instagram DM, or Messenger chat. You get clear, step-by-step guidance; if it needs a human, the handoff includes full context—no repeating yourself.',
    'about.supportChannels.title': 'Instant product help via QR, WhatsApp, Instagram & Messenger — no apps, no waiting.',
    'about.supportChannels.body':
      'CAIRE puts real support inside the product. Scan a QR, send a WhatsApp, Instagram DM, or Messenger chat. You get clear, step-by-step guidance; if it needs a person, the handoff carries full context—without repeating yourself.',
    'about.howItWorks.title': 'How it works',
    'about.howItWorks.card1.title': 'Per-model agent.',
    'about.howItWorks.card1.description': 'Each product gets its own AI agent trained on that exact model and its real failure modes.',
    'about.howItWorks.card2.title': 'Understands context.',
    'about.howItWorks.card2.description': 'Reads photos/video, error codes, and prior steps to give precise guidance.',
    'about.howItWorks.card3.title': 'Clean escalation.',
    'about.howItWorks.card3.description': 'If a fix needs a person, CAIRE passes full context to the brand—no repeated questions.',
    'about.howItWorks.card4.title': 'Closed-loop insights.',
    'about.howItWorks.card4.description': 'Brands see where customers get stuck and fix issues upstream.',
    'about.howItWorks.card5.title': 'Reliability first.',
    'about.howItWorks.card5.description': 'Clear steps, verifiable actions, and guardrails designed to earn trust.',
    'about.ourStory.title': 'Our story',
    'about.ourStory.content': 'We started CAIRE in 2024 to move support out of websites and queues and into the product itself. After years building product and engineering teams, we kept seeing the same truth: speed, clarity, and trust beat ticket numbers every time. CAIRE is our way to make that the default—help in seconds, learning from every interaction.',
    'about.team.title': 'The team',
    'about.team.subtitle': 'We\'re a small, distributed group focused on shipping reliable, real-world AI.',
    'about.team.fouad.name': 'Fouad Hamaidouch — Founder & CEO',
    'about.team.fouad.description': 'Built and led product & engineering teams for two decades. Believes support should feel human, be instant, and build trust.',
    'about.team.engineeringTitle': 'Founding Engineering Team',
    'about.team.mohammad.name': 'Mohammad Mohtashim Khan — Senior AI Engineer',
    'about.team.mohammad.description': 'Multi-agent workflows, LangGraph, and edge-case hardening. Ships fast; hates flaky experiences.',
    'about.team.businessTitle': 'Founding Business Team',
    'about.team.abie.name': 'Abie Sentani — Business Development Manager',
    'about.team.abie.description': 'Oversees business development in Indonesia. Builds strategic partnerships with leading brands in the region.',
    'about.team.tegar.name': 'Tegar Abriyan — Business Development Manager',
    'about.team.tegar.description': 'Drives business growth in the Indonesian market. Connects brands with innovative AI support solutions.',
    'about.team.footer': 'If you care about product experience and practical AI, say hi.',
    'about.beliefs.title': 'What we believe',
    'about.beliefs.card1.title': 'Speed matters',
    'about.beliefs.card1.description': 'Help should arrive now, not after a queue.',
    'about.beliefs.card2.title': 'Clarity beats clever',
    'about.beliefs.card2.description': 'Plain language, step-by-step.',
    'about.beliefs.card3.title': 'Earn trust',
    'about.beliefs.card3.description': 'Reliable beats impressive—every time.',
    'about.beliefs.card4.title': 'Learn and improve',
    'about.beliefs.card4.description': 'Every interaction should make the system smarter.',
    'about.milestones.title': 'Milestones',
    'about.milestones.march2024': 'March 2024',
    'about.milestones.march2024.description': 'CAIRE was founded with a bold idea: what if product support could live inside the product itself?',
    'about.milestones.january2025': 'January 2025',
    'about.milestones.january2025.description': 'We launched the first AI product agent for cosmetics and consumer products — replacing manuals with instant, human-like help.',
    'about.milestones.april2025': 'April 2025',
    'about.milestones.april2025.description': 'A small but determined team came together across time zones, united by one mission: make product care effortless.',
    'about.milestones.may2025': 'May 2025',
    'about.milestones.may2025.description': 'Speedinvest joined the journey, backing our vision to bring reliable, real-world AI into homes worldwide.',
    'about.milestones.endof2025': 'Late 2025',
    'about.milestones.endof2025.description': 'Launch pilots with leading cosmetic and DTC brands — bringing "Just Ask CAIRE" to life on WhatsApp.',
    'about.whereWeAreHeaded.title': 'Where we\'re headed',
    'about.whereWeAreHeaded.content': 'Support won\'t live on a website anymore. It will live with the product—always available, always helpful, always improving.',
    'about.whereWeAreHeaded.footer': 'No app. No manual. Support that lives with your product.',
    'about.whereWeAreHeaded.footerBold': 'Just Ask CAIRE.',
    'about.partners.backedBy': 'Backed by',
    'about.hiring.title': 'We\'re Hiring',
    'about.hiring.subtitle': 'Open Positions',
    'about.hiring.job1.title': 'Business Development Manager — Indonesia',
    'about.hiring.job1.description': 'Drive growth and partnerships in the Indonesian market. Build relationships with brands and expand CAIRE\'s presence.',
    'about.hiring.job1.cta': 'View on LinkedIn',
    'about.hiring.job2.title': 'Business Development Manager — Netherlands',
    'about.hiring.job2.description': 'Expand CAIRE\'s presence in the Dutch market. Build strategic partnerships and drive growth with leading brands.',
    'about.hiring.job2.cta': 'View on LinkedIn',
    'about.hiring.job3.title': 'AI Agent Trainer',
    'about.hiring.job3.description': 'Train and optimize AI agents for real products. Help CAIRE get smarter with every interaction.',
    'about.hiring.job3.cta': 'Contact us',
    'about.hiring.job4.title': 'Brand Success Lead',
    'about.hiring.job4.description': 'Own relationships with leading brands. Make sure their products and CAIRE work together brilliantly.',
    'about.hiring.job4.cta': 'Contact us',
    'about.hiring.footer': 'If you care about product experience and practical AI, reach out.',
    'about.cta.title': 'Build exceptional product experiences',
    'about.cta.subtitle': 'See how CAIRE can transform your product support.',
    'about.cta.button': 'Book demo',
  },

  id: {
    // Header
    'header.bookDemo': 'Pesan Demo',
    'header.joinPilot': 'Gabung Pilot',
    'header.pricing': 'Harga',
    'header.about': 'Tentang Kami',
    'header.signIn': 'Sign in',

    // VerifiedKnowledge (Hero) - Cosmetics focused
    'hero.badge': 'AI Product Experience untuk Kosmetik',
    'hero.title': 'AI customer service',
    'hero.titleLine2': 'untuk brand kosmetik.',
    'hero.subtitle': 'AI CS pintar yang paham produk kamu dari A sampai Z.',
    'hero.benefit1': 'Mengurangi barang retur',
    'hero.benefit2': 'Repeat order naik',
    'hero.benefit3': 'Pengalaman produk lebih baik',
    'hero.cta': 'Jadwalkan demo',

    // Credibility Strip
    'credibility.title': 'Dibuat untuk brand kosmetik yang peduli kepercayaan pelanggan.',

    // QR Code Generator Section
    'qrCode.badge': 'GENERATOR KODE QR',
    'qrCode.title': 'Satu QR. Dukungan instan.',
    'qrCode.subtitle': 'Buat kode QR untuk setiap produk. Pelanggan scan, masuk ke halaman branded, dan langsung dapat bantuan AI.',
    'qrCode.products': 'Produk',
    'qrCode.active': 'Aktif',
    'qrCode.draft': 'Draft',
    'qrCode.selectProduct': 'Pilih produk untuk buat QR',
    'qrCode.createQR': 'Buat Kode QR',
    'qrCode.qrFor': 'Kode QR untuk halaman landing',
    'qrCode.landingPage': 'Halaman Landing Produk',
    'qrCode.aiSupport': 'Dukungan AI, 24/7',
    'qrCode.downloadQR': 'Unduh Kode QR',
    'qrCode.scanInfo': 'Pelanggan scan & masuk ke sini',
    'qrCode.chatWith': 'Chat dengan CAIRE',
    'qrCode.available': 'Tersedia 24/7',
    'qrCode.aiChat': 'AI Chat + Sertifikasi',
    'qrCode.downloadAll': 'Semua bisa diunduh',
    'qrCode.feature1': 'Unik per produk',
    'qrCode.feature1Desc': 'Setiap produk punya QR sendiri',
    'qrCode.feature2': 'Halaman branded',
    'qrCode.feature2Desc': 'Logo Anda, produk Anda',
    'qrCode.feature3': 'Dukungan AI instan',
    'qrCode.feature3Desc': 'Scan → Chat → Selesai',

    // QR Generator (new keys)
    'qrGenerator.badge': 'GENERATOR KODE QR',
    'qrGenerator.title': 'Satu QR. Dukungan instan.',
    'qrGenerator.subtitle': 'Buat kode QR untuk setiap produk. Pelanggan scan, masuk ke halaman branded, dan langsung dapat bantuan AI.',
    'qrGenerator.products': 'Produk',
    'qrGenerator.active': 'Aktif',
    'qrGenerator.generateTitle': 'Buat Kode QR',
    'qrGenerator.generateDesc': 'Untuk Everwhite Axillary Cream',
    'qrGenerator.landingPage': 'Halaman Landing Produk',
    'qrGenerator.landingPageDesc': 'Dukungan AI, 24/7',
    'qrGenerator.downloadQR': 'Unduh Kode QR',
    'qrGenerator.customerScans': 'Pelanggan scan & masuk ke sini',
    'qrGenerator.chatWithCaire': 'Chat dengan CAIRE',
    'qrGenerator.available247': 'Tersedia 24/7',
    'qrGenerator.aiChatCerts': 'AI Chat + Sertifikasi',
    'qrGenerator.allDownloadable': 'Semua bisa diunduh',
    'qrGenerator.uniquePerProduct': 'Unik per produk',
    'qrGenerator.uniqueDesc': 'Setiap produk punya QR sendiri',
    'qrGenerator.brandedPages': 'Halaman branded',
    'qrGenerator.brandedDesc': 'Logo Anda, produk Anda',
    'qrGenerator.instantSupport': 'Dukungan AI instan',
    'qrGenerator.instantDesc': 'Scan → Chat → Selesai',

    // Omnichannel Inbox Section
    'inbox.badge': 'INBOX OMNICHANNEL',
    'inbox.title': 'Satu inbox. Semua channel.',
    'inbox.subtitle': 'WhatsApp, Instagram, Messenger, dan chatbox website—semua percakapan di satu tempat. AI tangani volume, Anda tetap kontrol.',
    'inbox.ai': 'AI',
    'inbox.search': 'Cari',
    'inbox.ongoing': 'Ongoing',
    'inbox.aiCustomerSummary': 'AI Customer Summary',
    'inbox.customerInfo': 'Info Pelanggan',
    'inbox.conversationFlow': 'Alur Percakapan',
    'inbox.greetingContext': 'Greeting & Context',
    'inbox.informationRequest': 'Information Request',
    'inbox.privacyNotice': 'Privacy Notice',
    'inbox.solutionProvided': 'Solution Provided',
    'inbox.humanEscalation': 'Human Escalation',
    'inbox.done': 'Done',
    'inbox.active': 'Active',
    'inbox.pending': 'Pending',
    'inbox.handledByAI': 'Handled by AI Agent',
    'inbox.aiHandling': 'AI sedang menangani - Ambil alih untuk merespons',
    'inbox.allConversations': 'Semua percakapan mengalir ke satu inbox',
    'inbox.qrProductPage': 'Halaman Produk QR',
    'inbox.whatsapp': 'WhatsApp',
    'inbox.instagram': 'Instagram',
    'inbox.messenger': 'Messenger',
    'inbox.webChat': 'Web Chat',

    // AI Inbox (new keys)
    'aiInbox.badge': 'INBOX OMNICHANNEL',
    'aiInbox.title': 'Satu inbox. Semua channel.',
    'aiInbox.subtitle': 'WhatsApp, Instagram, Messenger, dan chatbox website—semua percakapan di satu tempat. AI tangani volume, Anda tetap kontrol.',
    'aiInbox.inbox': 'Inbox',
    'aiInbox.customerInfo': 'Info Pelanggan',
    'aiInbox.conversationFlow': 'Alur Percakapan',
    'aiInbox.aiHandling': 'AI sedang menangani - Ambil alih untuk merespons',
    'aiInbox.allFlowIntoOne': 'Semua percakapan mengalir ke satu inbox',
    'aiInbox.qrProductPages': 'Halaman Produk QR',

    // Website Chat Widget Section
    'chatWidget.badge': 'WEBSITE CHAT WIDGET',
    'chatWidget.title': 'Embed CAIRE on your website',
    'chatWidget.subtitle': 'Give your customers instant AI-powered support directly on your website. No app downloads, no redirects—just seamless conversations.',
    'chatWidget.onlineNow': 'Online now',
    'chatWidget.typeMessage': 'Type a message...',
    'chatWidget.poweredBy': 'Powered by Caire',
    'chatWidget.feature1': 'One-line embed code',
    'chatWidget.feature2': 'Matches your brand',
    'chatWidget.feature3': 'Image uploads',
    'chatWidget.feature4': '24/7 AI responses',

    // AI Page Builder Section
    'pageBuilder.badge': 'AI PAGE BUILDER',
    'pageBuilder.title': 'Bangun halaman produk dengan AI',
    'pageBuilder.subtitle': 'Setiap produk punya halaman landing sendiri. Edit dengan bahasa natural—tanpa coding.',
    'pageBuilder.editor': 'Landing Page Editor',
    'pageBuilder.builder': 'Landing Page Builder',
    'pageBuilder.pageBuilderAI': 'Page Builder AI',
    'pageBuilder.askToChange': 'Minta saya untuk ubah halaman landing Anda',
    'pageBuilder.preview': 'Preview',
    'pageBuilder.chatOrTalk': 'Chat atau Bicara dengan CAIRE',
    'pageBuilder.available247': 'Tersedia 24/7',
    'pageBuilder.halal': 'Halal',
    'pageBuilder.bpom': 'BPOM',
    'pageBuilder.explainChange': 'Jelaskan apa yang ingin Anda ubah...',
    'pageBuilder.feature1': 'Edit dengan AI',
    'pageBuilder.feature2': 'Preview instan',
    'pageBuilder.feature3': 'Tanpa coding',
    'pageBuilder.feature4': 'Support multi-bahasa',

    // AI Page Builder (new keys)
    'aiPageBuilder.badge': 'AI PAGE BUILDER',
    'aiPageBuilder.title': 'Bangun halaman produk dengan AI',
    'aiPageBuilder.subtitle': 'Setiap produk punya halaman landing sendiri. Edit dengan bahasa natural—tanpa coding.',
    'aiPageBuilder.landingPageBuilder': 'Landing Page Builder',
    'aiPageBuilder.pageBuilderAI': 'Page Builder AI',
    'aiPageBuilder.askToModify': 'Minta saya untuk ubah halaman landing Anda',
    'aiPageBuilder.describeChange': 'Jelaskan apa yang ingin Anda ubah...',
    'aiPageBuilder.preview': 'Preview',
    'aiPageBuilder.feature1': 'Edit dengan AI',
    'aiPageBuilder.feature2': 'Preview instan',
    'aiPageBuilder.feature3': 'Tanpa coding',
    'aiPageBuilder.feature4': 'Support multi-bahasa',

    // CAIRE Comparison - Cosmetics focused
    'comparison.badge': 'Bingung → Percaya',
    'comparison.title': 'Produknya bagus — tapi pelanggan butuh jawaban lebih cepat.',
    'comparison.subtitle': 'Pelanggan tidak return karena produk jelek. Mereka return karena tidak dapat jawaban cukup cepat.',
    'comparison.headerBefore': 'Tanpa CAIRE',
    'comparison.headerAfter': 'Dengan CAIRE',
    'comparison.item1Before': '"Bahan ini aman nggak buat kulit aku?"',
    'comparison.item1After': 'Info bahan & keamanan instan via chat',
    'comparison.item2Before': '"Bingung cara pakai serum ini"',
    'comparison.item2After': 'Tutorial step-by-step di WhatsApp',
    'comparison.item3Before': 'DM penuh pertanyaan dasar',
    'comparison.item3After': '70% pertanyaan terjawab otomatis',
    'comparison.item4Before': 'Tidak ada bukti sertifikasi halal/BPOM',
    'comparison.item4After': 'Sertifikasi ditampilkan langsung',
    'comparison.item5Before': 'Review buruk dari pelanggan bingung',
    'comparison.item5After': 'Kepuasan tinggi & repeat purchase',
    'comparison.item6Before': 'Tidak tahu apa yang pelanggan tanya',
    'comparison.item6After': 'Insight nyata dari setiap produk',
    'comparison.footer': 'Dibuat untuk brand kosmetik yang peduli kepercayaan pelanggan.',
    'comparison.cta': 'Mulai sekarang',

    // Platform Integrations
    'integrations.badge': 'Integrasi Platform',
    'integrations.title': 'Hubungkan katalog produk dalam hitungan menit',
    'integrations.description': 'Data produk masuk — agen AI produk keluar. Tanpa setup manual.',

    // Meta Partner Banner
    'metaPartner.badge': 'PARTNER TEKNOLOGI META RESMI',
    'metaPartner.description': 'Partner Resmi Meta yang memberikan dukungan pelanggan cerdas berbasis AI melalui WhatsApp Business — didukung oleh CAIRE.',

    // Testimonials - Cosmetics focused
    'testimonials.title': 'Pertanyaan nyata, jawaban instan',
    'testimonials.subtitle': 'Lihat bagaimana CAIRE bantu pelanggan kosmetik dapat jawaban secara instan',
    'testimonials.quote1': '"Klien maklon kami suka karena brand mereka sekarang punya product support instan. Bikin produk mereka terasa premium."',
    'testimonials.author1': '',
    'testimonials.quote2': '"Pelanggan dulu sering DM soal bahan-bahan. Sekarang CAIRE handle 80% pertanyaan itu secara otomatis."',
    'testimonials.author2': '',
    'testimonials.quote3': '"Kami kurangi tingkat retur dengan menampilkan sertifikat halal dan BPOM langsung lewat chatbot."',
    'testimonials.author3': '',

    // Pricing
    'pricing.title': 'Harga yang tumbuh bersama produk Anda',
    'pricing.subtitle': 'Mulai gratis. Tambahkan white-label, dan otomasi seiring Anda bertumbuh.',
    'pricing.noContracts': 'Tanpa kontrak jangka panjang. Upgrade kapan saja.',
    'pricing.planFree': 'Mulai',
    'pricing.planGrowth': 'Growth',
    'pricing.planEnterprise': 'Enterprise',
    'pricing.freeTagline': 'Mulai cepat',
    'pricing.growthTagline': 'Tumbuh sesuai skala',
    'pricing.enterpriseTagline': 'Otomasi dalam skala besar',
    'pricing.freePeriod': '',
    'pricing.growthPeriod': '',
    'pricing.enterprisePeriod': '',
    'pricing.freePrice': 'Gratis',
    'pricing.growthPrice': 'Bayar Sesuai Pemakaian',
    'pricing.enterprisePrice': 'Custom',
    'pricing.freeDescription': 'Luncurkan asisten AI produk pertama Anda dalam hitungan menit. Tanpa risiko, tanpa kartu kredit.',
    'pricing.growthDescription': 'Skalakan berdasarkan penggunaan aktual. Tanpa biaya bulanan tetap.',
    'pricing.enterpriseDescription': 'AI white-label untuk seluruh katalog produk — dibuat untuk OEM, enterprise & pasar teregulasi.',
    'pricing.freeFeature1': 'Hingga 5 agen AI produk',
    'pricing.freeFeature2': 'Agen WhatsApp/Instagram/Messenger/Chatbox CAIRE',
    'pricing.freeFeature3': 'Pelatihan pengetahuan produk',
    'pricing.freeFeature4': 'Aktivasi QR per produk',
    'pricing.freeFeature5': 'Riwayat percakapan',
    'pricing.freeFeature6': 'Integrasi Slack',
    'pricing.freeFeature7': 'Agen AI dalam 1 bahasa',
    'pricing.freeFeature8': '1 kursi tim',
    'pricing.freeFeature9': 'Email support',
    'pricing.growthFeature1': 'Bayar hanya yang Anda gunakan',
    'pricing.growthFeature2': 'Agen WhatsApp/Instagram/Messenger/Chatbox white-label',
    'pricing.growthFeature3': 'Nomor WhatsApp/Instagram/Messenger milik brand',
    'pricing.growthFeature4': 'Workflow builder',
    'pricing.growthFeature5': 'Analytics Lite',
    'pricing.growthFeature6': 'Handover agen',
    'pricing.growthFeature7': 'Shopify, Zendesk & Intercom',
    'pricing.growthFeature8': 'Agen AI multi-bahasa',
    'pricing.growthFeature9': '3 kursi tim',
    'pricing.enterpriseFeature1': 'Agen AI produk unlimited',
    'pricing.enterpriseFeature2': 'Otomasi workflow lanjutan',
    'pricing.enterpriseFeature3': 'Integrasi custom & akses API',
    'pricing.enterpriseFeature4': 'Web + WhatsApp/Instagram/Messenger terpadu',
    'pricing.enterpriseFeature5': 'Manajemen tim & audit logs',
    'pricing.enterpriseFeature6': 'Analytics lanjutan',
    'pricing.enterpriseFeature7': 'Routing prioritas & eskalasi',
    'pricing.enterpriseFeature8': 'Agen AI multi-bahasa',
    'pricing.enterpriseFeature9': 'Region data EU/US/Asia',
    'pricing.enterpriseFeature10': 'SSO (Okta, Google)',
    'pricing.enterpriseFeature11': 'SLA & jaminan uptime',
    'pricing.enterpriseFeature12': 'Success manager khusus',
    'pricing.freeCta': 'Gabung waitlist',
    'pricing.growthCta': 'Mulai sekarang',
    'pricing.enterpriseCta': 'Jadwalkan demo',
    'pricing.seeFullPricing': 'Lihat harga lengkap',

    // Pricing Calculator
    'calculator.title': 'Estimasi biaya Anda',
    'calculator.subtitle': 'Harga yang sederhana dan transparan berdasarkan penggunaan',
    'calculator.whatsappLabel': 'Chat WhatsApp/Instagram/Messenger/Chatbox per bulan',
    'calculator.estimatedCost': 'Estimasi biaya bulanan',
    'calculator.perMonth': 'per bulan',
    'calculator.savingsTitle': 'Estimasi Penghematan Bulanan',
    'calculator.savingsSubtitle': 'Apa artinya bagi brand Anda',
    'calculator.benefit1': 'Pelanggan mendapat bantuan WhatsApp/Instagram/Messenger/Chatbox instan 24/7',
    'calculator.benefit2': 'Tim Anda menghemat {hours}+ jam per bulan',
    'calculator.benefit3': 'Anda memangkas biaya support manual hingga 85%',

    // Feature Comparison
    'comparison.features': 'Fitur',
    'comparison.getStarted': 'Mulai',
    'comparison.growth': 'Growth',
    'comparison.enterprise': 'Enterprise',
    'comparison.aiAgents': 'Agen AI produk',
    'comparison.basePrice': 'Harga dasar',
    'comparison.whatsappAgent': 'Agen AI WhatsApp/Instagram/Messenger/Chatbox',
    'comparison.qrActivation': 'Aktivasi QR per produk',
    'comparison.workflowBuilder': 'AI workflow builder',
    'comparison.handover': 'Handover ke tim support',
    'comparison.shopifyIntegration': 'Integrasi Shopify',
    'comparison.zendeskIntercom': 'Zendesk / Intercom',
    'comparison.analytics': 'Analytics',
    'comparison.teamSeats': 'Kursi tim',
    'comparison.apiIntegration': 'API & integrasi custom',
    'comparison.ssoSla': 'SSO, SLA, Region data (EU/US/Asia)',
    'comparison.security': 'Keamanan & tata kelola',
    'comparison.dpa': 'Perjanjian pemrosesan data (DPA)',
    'comparison.slaUptime': 'SLA (uptime)',
    'comparison.compliance': 'Sertifikasi kepatuhan',
    'comparison.dataRetention': 'Retensi data',
    'comparison.upTo5': 'Hingga 5',
    'comparison.unlimited': 'Unlimited',
    'comparison.free': 'Gratis',
    'comparison.payAsYouGo': 'Bayar sesuai pemakaian',
    'comparison.custom': 'Custom',
    'comparison.lite': 'Lite',
    'comparison.advanced': 'Lanjutan',
    'comparison.days14': '14 hari',
    'comparison.days180': '180 hari',
    'comparison.months24plus': '24 bulan+',
    'comparison.soc2gdpr': 'SOC2, GDPR',
    'comparison.soc2iso27001': 'SOC2, ISO 27001, GDPR',
    'comparison.uptime995': '99.5%',

    // Pricing FAQ
    'faq.title': 'Pertanyaan yang Sering Diajukan',
    'faq.q1': 'Bisakah saya mulai tanpa pekerjaan engineering?',
    'faq.a1': 'Tentu. Semua paket CAIRE sepenuhnya no-code — itulah mengapa kami membangun CAIRE. Cukup unggah informasi produk Anda, latih AI, dan mulai membantu pelanggan dalam hitungan menit. Tanpa setup teknis, tanpa integrasi, tanpa code.',
    'faq.q2': 'Apa perbedaan antara agen CAIRE dan agen White-label?',
    'faq.a2': 'Agen CAIRE (WhatsApp/Instagram/Messenger/Chatbox)\nPelanggan berbicara dengan support Anda melalui WhatsApp/Instagram/Messenger/Chatbox dengan branding CAIRE. Cepat diluncurkan, mudah, sempurna untuk pilot.\n\nAgen White-label\nPelanggan berbicara dengan "Anda" di channel Anda sendiri (WhatsApp/Instagram/Messenger/Chatbox), dengan logo dan branding Anda. Terasa seperti tim support Anda sendiri yang didukung oleh CAIRE.',
    'faq.q3': 'Bisakah saya menggunakan akun WhatsApp/Instagram/Messenger yang sudah ada?',
    'faq.a3': 'Ya. Di paket Growth dan Enterprise, Anda bisa menghubungkan nomor WhatsApp Business, akun Instagram Business, dan Messenger yang sudah ada ke CAIRE, jadi pelanggan tetap menggunakan channel yang sama. Kami tangani integrasinya agar mulus.\n\nLebih suka simpan akun lama untuk penggunaan internal?\nTidak masalah. Kami juga bisa set up channel baru untuk agen CAIRE Anda.',
    'faq.q4': 'Apakah Anda mendukung sistem ticketing seperti Zendesk?',
    'faq.a4': 'Ya. Anda tetap bisa pakai tools seperti Zendesk, Intercom.\nCAIRE jadi lapisan pertama product support Anda di WhatsApp/Instagram/Messenger/Chatbox.\n\nPelanggan tidak perlu isi form atau buat tiket sendiri.\nCAIRE kumpulkan detailnya, selesaikan yang bisa, dan otomatis buat tiket di sistem Anda kalau dibutuhkan, dengan semua konteks percakapan terlampir.\n\nTim support Anda lanjut dari mana CAIRE berhenti — tanpa ganggu workflow Anda.',
    'faq.q6': 'Apakah Anda mendukung banyak bahasa?',
    'faq.a6': 'Ya. Agen AI CAIRE adalah multilingual.\n\nPaket Free mendukung satu bahasa.\n\nGrowth dan Enterprise buka multi-language support, jadi pelanggan Anda bisa dapat bantuan dalam bahasa pilihan mereka secara otomatis.\n\nKami saat ini mendukung:\n- Bahasa Indonesia\n- English\n- Nederlands\n- Español\n- Français\n- Deutsch\n\nBahasa lainnya segera hadir.',
    'faq.q7': 'Apakah Anda menyediakan dukungan lokal di Indonesia?',
    'faq.a7': 'Tentu! Kami berbasis di Indonesia untuk memastikan Anda mendapat support terbaik. Tim Indonesia kami memahami pasar lokal dan bisa bantu Anda dengan implementasi, technical support, dan training. Kami di sini untuk bantu kesuksesan Anda dengan CAIRE.',
    'faq.q8': 'Apakah Anda menyediakan dukungan lokal di Eropa?',
    'faq.a8': 'Ya, kami berbasis di Belanda dan memiliki tim yang siap mendukung Anda di seluruh Eropa. Kami memahami pasar Eropa dan dapat membantu Anda dengan implementasi, technical support, dan pelatihan di region Anda.',

    // Pricing CTA
    'pricingCta.title': 'Mulai sekarang',
    'pricingCta.subtitle': 'Transformasi dukungan produk Anda dengan AI yang memahami produk Anda.',
    'pricingCta.button': 'Jadwalkan demo',

    // Enterprise Contact
    'enterprise.title': 'AI support untuk skala enterprise',
    'enterprise.subtitle': 'Custom integration, security review, dan onboarding termasuk.',
    'enterprise.subtitleLine2': 'Bicara dengan tim kami untuk desain deployment ideal Anda.',
    'enterprise.firstNameLabel': 'Nama depan',
    'enterprise.lastNameLabel': 'Nama belakang',
    'enterprise.emailLabel': 'Email*',
    'enterprise.submitButton': 'Kirim',
    'enterprise.submitting': 'Mengirim...',
    'enterprise.successTitle': 'Terima kasih atas minat Anda!',
    'enterprise.successMessage': 'Kiriman Anda telah diterima. Tim kami akan menghubungi dalam 24 jam untuk membahas kebutuhan enterprise Anda.',

    // Footer
    'footer.terms': 'Syarat & Ketentuan',
    'footer.privacy': 'Privasi',
    'footer.copyright': '© 2026 CAIRE. Hak cipta dilindungi.',

    // About Page
    'about.badge': 'Cerita Kami',
    'about.hero.title': 'Tentang CAIRE',
    'about.hero.subtitle': 'Bantuan produk instan lewat QR, WhatsApp, Instagram & Messenger — tanpa aplikasi, tanpa menunggu.',
    'about.hero.description': 'CAIRE menaruh support nyata di dalam produk. Scan QR, kirim WhatsApp, Instagram DM, atau Messenger chat. Anda dapat panduan jelas langkah demi langkah; kalau butuh manusia, semua konteks diteruskan—tanpa perlu mengulangi.',
    'about.supportChannels.title': 'Bantuan produk instan lewat QR, WhatsApp, Instagram & Messenger — tanpa aplikasi, tanpa menunggu.',
    'about.supportChannels.body':
      'CAIRE menaruh support nyata di dalam produk. Scan QR, kirim WhatsApp, Instagram DM, atau Messenger chat. Anda dapat panduan jelas langkah demi langkah; kalau butuh manusia, semua konteks diteruskan—tanpa perlu mengulangi.',
    'about.howItWorks.title': 'Cara kerjanya',
    'about.howItWorks.card1.title': 'AI yang dirancang khusus untuk produk Anda.',
    'about.howItWorks.card1.description': 'Setiap produk memiliki asisten pintar yang memahami detail produk, masalah umum, dan pertanyaan pelanggan sebenarnya.',
    'about.howItWorks.card2.title': 'Memahami kebutuhan pelanggan secara langsung.',
    'about.howItWorks.card2.description': 'CAIRE bisa baca gambar, video, atau kode error untuk bimbing pelanggan step by step — tanpa tebak-tebakan.',
    'about.howItWorks.card3.title': 'Handover yang mulus.',
    'about.howItWorks.card3.description': 'Saat pelanggan membutuhkan bantuan manusia, CAIRE secara otomatis mengirimkan seluruh konteks percakapan ke tim Anda — sehingga pelanggan tidak perlu menjelaskan ulang.',
    'about.howItWorks.card4.title': 'Dibangun untuk trust.',
    'about.howItWorks.card4.description': 'Setiap jawaban mengikuti langkah yang sudah diverifikasi dengan panduan yang jelas — membuat pelanggan merasa aman dan yakin saat menggunakannya.',
    'about.howItWorks.card5.title': 'Reliability first.',
    'about.howItWorks.card5.description': 'Langkah yang jelas, tindakan yang terverifikasi, dan guardrails yang dirancang untuk membangun kepercayaan.',
    'about.ourStory.title': 'Cerita kami',
    'about.ourStory.content': 'Kami mulai CAIRE setelah bertahun-tahun melihat pelanggan stuck — bukan karena produknya rusak, tapi karena support-nya terpisah. Tiket, chatbot, manual — semua terputus dari produk itu sendiri. Karena itu kami menciptakan CAIRE — lapisan AI yang hidup di dalam setiap produk, siap membantu secara instan tanpa perlu unduhan dan tanpa menunggu.',
    'about.team.title': 'Tim',
    'about.team.subtitle': 'Membangun masa depan product support dengan tenang.',
    'about.team.fouad.name': 'Fouad Hamaidouch — Founder & CEO',
    'about.team.fouad.description': 'Membangun dan memimpin tim produk & engineering selama dua dekade. Percaya support harus terasa manusiawi, instan, dan membangun trust.',
    'about.team.engineeringTitle': 'Tim Founding Engineering',
    'about.team.mohammad.name': 'Mohammad Mohtashim Khan — Senior AI Engineer',
    'about.team.mohammad.description': 'Multi-agent workflows, LangGraph, dan edge-case hardening. Ship cepat; benci pengalaman yang flaky.',
    'about.team.businessTitle': 'Tim Founding Business',
    'about.team.abie.name': 'Abie Sentani — Business Development Manager',
    'about.team.abie.description': 'Mengawasi business development di Indonesia. Membangun partnership strategis dengan brand terkemuka di region.',
    'about.team.tegar.name': 'Tegar Abriyan — Business Development Manager',
    'about.team.tegar.description': 'Mendorong pertumbuhan bisnis di pasar Indonesia. Menghubungkan brand dengan solusi AI support inovatif.',
    'about.team.footer': 'Jika Anda peduli pengalaman produk dan AI praktis, sapa kami.',
    'about.beliefs.title': 'Apa yang kami yakini',
    'about.beliefs.card1.title': 'Kecepatan penting',
    'about.beliefs.card1.description': 'Bantuan harus datang sekarang, bukan setelah antri.',
    'about.beliefs.card2.title': 'Jelas lebih baik dari pintar',
    'about.beliefs.card2.description': 'Bahasa sederhana, langkah demi langkah.',
    'about.beliefs.card3.title': 'Bangun kepercayaan',
    'about.beliefs.card3.description': 'Reliable mengalahkan impressive—setiap saat.',
    'about.beliefs.card4.title': 'Belajar dan berkembang',
    'about.beliefs.card4.description': 'Setiap interaksi harus membuat sistem lebih pintar.',
    'about.milestones.title': 'Milestone',
    'about.milestones.march2024': 'Maret 2024',
    'about.milestones.march2024.description': 'CAIRE didirikan dengan ide berani: bagaimana kalau product support bisa hidup di dalam produknya sendiri?',
    'about.milestones.january2025': 'Januari 2025',
    'about.milestones.january2025.description': 'Kami perkenalkan agen AI produk pertama untuk kosmetik dan produk konsumen — mengganti manual dengan bantuan instan yang seperti manusia.',
    'about.milestones.april2025': 'April 2025',
    'about.milestones.april2025.description': 'Tim kecil tapi determined berkumpul lintas zona waktu, bersatu dengan satu misi: bikin product care jadi mudah.',
    'about.milestones.may2025': 'Mei 2025',
    'about.milestones.may2025.description': 'Speedinvest bergabung dalam perjalanan ini, mendukung visi kami membawa AI yang reliable dan real-world ke rumah-rumah di seluruh dunia.',
    'about.milestones.endof2025': 'Akhir 2025',
    'about.milestones.endof2025.description': 'Luncurkan pilot dengan brand kosmetik dan DTC terkemuka — hidupkan "Cukup tanya CAIRE" di WhatsApp.',
    'about.whereWeAreHeaded.title': 'Ke mana arah kami',
    'about.whereWeAreHeaded.content': 'Support nggak akan hidup di website lama lagi. Dia akan hidup dengan produk—selalu tersedia, selalu helpful, selalu improving.',
    'about.whereWeAreHeaded.footer': 'Tanpa aplikasi. Tanpa manual. Support yang hidup dengan produk.',
    'about.whereWeAreHeaded.footerBold': 'Cukup tanya CAIRE.',
    'about.partners.backedBy': 'Didukung oleh',
    'about.hiring.title': 'Kami Merekrut',
    'about.hiring.subtitle': 'Posisi Terbuka',
    'about.hiring.job1.title': 'Business Development Manager — Indonesia',
    'about.hiring.job1.description': 'Dorong pertumbuhan dan partnership di pasar Indonesia. Bangun relasi dengan brand dan perluas kehadiran CAIRE.',
    'about.hiring.job1.cta': 'Lihat di LinkedIn',
    'about.hiring.job2.title': 'Business Development Manager — Belanda',
    'about.hiring.job2.description': 'Perluas kehadiran CAIRE di pasar Belanda. Bangun partnership strategis dan dorong pertumbuhan brand terkemuka.',
    'about.hiring.job2.cta': 'Lihat di LinkedIn',
    'about.hiring.job3.title': 'AI Agent Trainer',
    'about.hiring.job3.description': 'Latih dan optimasi AI agents untuk produk nyata. Bantu CAIRE jadi lebih pintar dengan setiap interaksi.',
    'about.hiring.job3.cta': 'Hubungi kami',
    'about.hiring.job4.title': 'Brand Success Lead',
    'about.hiring.job4.description': 'Kelola hubungan dengan partner. Pastikan produk mereka dan CAIRE bekerja dengan sempurna.',
    'about.hiring.job4.cta': 'Hubungi kami',
    'about.hiring.footer': 'Jika Anda peduli pengalaman produk dan AI praktis, sapa kami.',
    'about.cta.title': 'Bangun pengalaman produk yang luar biasa',
    'about.cta.subtitle': 'Lihat bagaimana CAIRE bisa transform dukungan produk Anda.',
    'about.cta.button': 'Jadwalkan demo',
  },
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguageState] = useState<'en' | 'id'>('en');

  // Sync language state with URL path
  useEffect(() => {
    const path = location.pathname;

    // Extract language from URL path (e.g., /en/about -> en, /id/pricing -> id)
    if (path.startsWith('/id')) {
      setLanguageState('id');
      localStorage.setItem('caire-language', 'id');
    } else if (path.startsWith('/en')) {
      setLanguageState('en');
      localStorage.setItem('caire-language', 'en');
    } else {
      // Default to English if no language prefix
      setLanguageState('en');
    }
  }, [location.pathname]);

  const setLanguage = (lang: 'en' | 'id') => {
    const currentPath = location.pathname;
    let newPath = '';

    // Remove current language prefix and replace with new one
    if (currentPath.startsWith('/en')) {
      newPath = currentPath.replace('/en', `/${lang}`);
    } else if (currentPath.startsWith('/id')) {
      newPath = currentPath.replace('/id', `/${lang}`);
    } else {
      // If no language prefix, add it
      newPath = `/${lang}${currentPath === '/' ? '' : currentPath}`;
    }

    // Navigate to the new path
    navigate(newPath, { replace: true });
    setLanguageState(lang);
    localStorage.setItem('caire-language', lang);
  };

  const t = (key: string): string => {
    const value = translations[language][key as keyof typeof translations['en']];
    return value !== undefined ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}
